import { validate } from "uuid";
import { addParentheses } from "../../MDLSegments/utils/SegmentDefinitionGenerator.js";
import { CREATE_EXPERIENCE, METRIC_FUNCTION, FILL_OPTION, THRESHOLD_OPERATOR, AGGREGATION_TYPE } from "../models/MetricDefinitionUIModel.js";
import { MDL_METHOD, AGGREGATION_METHOD } from "../models/MDLMetricDefinitions.js";
const FillZero_Dict = {
  COUNT: MDL_METHOD.COUNTIf,
  COUNTDISTINCT: MDL_METHOD.COUNTDISTINCTIf,
  SUM: MDL_METHOD.SUMIf,
  Average: MDL_METHOD.AverageIf
};
const FillNull_Dict = {
  MAX: MDL_METHOD.MAXIf,
  MIN: MDL_METHOD.MINIf,
  COUNT: MDL_METHOD.ConditionalCOUNT,
  SUM: MDL_METHOD.ConditionalSUM,
  Average: MDL_METHOD.ConditionalAverage,
  Rate: MDL_METHOD.ConditionalRate
};
const negativeInfinity = "double.NegativeInfinity";
const positiveInfinity = "double.PositiveInfinity";
const mdlMetricDefinitionGenerator = (metric) => {
  if (metric.createExperience === CREATE_EXPERIENCE.metricWizard) {
    const fullMetricDef = metric.fullMetricDefinition;
    const mdlMetricDef = buildMetricDefinition(fullMetricDef.metricDefinition);
    const metricDefWithAggregation = addAggregations(fullMetricDef.aggregation, mdlMetricDef);
    return metricDefWithAggregation;
  } else if (metric.createExperience === CREATE_EXPERIENCE.rawMode) {
    return metric.fullMetricDefinition.definition;
  }
  return "";
};
const buildMetricDefinition = (metricDefinitionList) => {
  var _a, _b, _c;
  let lastMDL = "";
  let metricDefinitionMap = {};
  for (var metric of metricDefinitionList) {
    let currMDL = "";
    const filterCondition = metric.filters.columnFilter.column;
    const filterOption = metric.filters.columnFilter.fillOption;
    let metricNumerator = metric.numerator;
    let metricDenominator = metric.denominator;
    if (!!metricNumerator && validate(metricNumerator)) {
      metricNumerator = metricDefinitionMap[metricNumerator];
    }
    if (!!metricDenominator && validate(metricDenominator)) {
      metricDenominator = metricDefinitionMap[metricDenominator];
    }
    if (metric.function === METRIC_FUNCTION.rate) {
      if (filterCondition && filterOption === FILL_OPTION.null) {
        currMDL = buildMethod(FillNull_Dict[(_a = metric.function) == null ? void 0 : _a.toString()], metric.groupBy) + addParentheses([metricNumerator, filterCondition]);
      } else if (filterCondition && filterOption === FILL_OPTION.zero) {
        const conditionAndColumn = `${filterCondition} && ${metricNumerator}`;
        const numerator = buildMethod(MDL_METHOD.SUM, metric.groupBy) + addParentheses([booleanToInt(conditionAndColumn)]);
        const denominator = buildMethod(MDL_METHOD.COUNT, metric.groupBy) + addParentheses([metricNumerator]);
        currMDL = buildMethod(MDL_METHOD.SafeDivide) + addParentheses([numerator, denominator]);
      } else {
        const numerator = buildMethod(MDL_METHOD.SUM, metric.groupBy) + addParentheses([booleanToInt(metricNumerator)]);
        const denominator = buildMethod(MDL_METHOD.COUNT, metric.groupBy) + addParentheses([metricNumerator]);
        currMDL = buildMethod(MDL_METHOD.SafeDivide) + addParentheses([numerator, denominator]);
      }
    } else if (metric.function === METRIC_FUNCTION.ratio) {
      const numerator = buildMethod(MDL_METHOD.SUM, metric.groupBy) + addParentheses([metricNumerator]);
      const denominator = buildMethod(MDL_METHOD.SUM, metric.groupBy) + addParentheses([metricDenominator]);
      currMDL = buildMethod(MDL_METHOD.SafeDivide) + addParentheses([numerator, denominator]);
    } else if (metric.function === METRIC_FUNCTION.indicator) {
      if (filterCondition && filterOption === FILL_OPTION.null) {
        const ternaryCondition = buildMethod(MDL_METHOD.MAX, metric.groupBy) + addParentheses([booleanToInt(filterCondition)]);
        const ternaryOption1 = buildMethod(MDL_METHOD.MAX, metric.groupBy) + addParentheses([booleanToInt(metricNumerator)]);
        currMDL = `${ternaryCondition} == 1 ? ${ternaryOption1} : (double?)null`;
      } else if (filterCondition && filterOption === FILL_OPTION.zero) {
        const ternaryCondition = buildMethod(MDL_METHOD.MAX, metric.groupBy) + addParentheses([booleanToInt(filterCondition)]);
        const ternaryOption1 = buildMethod(MDL_METHOD.MAX, metric.groupBy) + addParentheses([booleanToInt(metricNumerator)]);
        currMDL = `${ternaryCondition} == 1 ? ${ternaryOption1} : 0`;
      } else {
        currMDL = buildMethod(MDL_METHOD.MAX, metric.groupBy) + addParentheses([booleanToInt(metricNumerator)]);
      }
    } else if (metric.function !== void 0) {
      if (filterCondition && filterOption === FILL_OPTION.null) {
        if (metric.function === METRIC_FUNCTION.countDistinct) {
          const ternaryCondition = buildMethod(MDL_METHOD.SUM, metric.groupBy) + addParentheses([booleanToInt(filterCondition)]);
          const ternaryOption1 = buildMethod(MDL_METHOD.COUNTDISTINCTIf, metric.groupBy) + addParentheses([metricNumerator, filterCondition]);
          currMDL = `${ternaryCondition} > 0 ? ${ternaryOption1} : (long?)null`;
        } else {
          currMDL = buildMethod(FillNull_Dict[(_b = metric.function) == null ? void 0 : _b.toString()], metric.groupBy) + addParentheses([metricNumerator, filterCondition]);
        }
      } else if (filterCondition && filterOption === FILL_OPTION.zero) {
        currMDL = buildMethod(FillZero_Dict[(_c = metric.function) == null ? void 0 : _c.toString()], metric.groupBy) + addParentheses([metricNumerator, filterCondition]);
      } else {
        currMDL = buildMethod(metric.function, metric.groupBy) + addParentheses([metricNumerator]);
      }
    }
    currMDL = addThresholds(metric.filters.thresholds, currMDL);
    metricDefinitionMap[metric.id] = currMDL;
    lastMDL = currMDL;
  }
  return lastMDL;
};
const addThresholds = (thresholds, innerMDL) => {
  let lowerBound = negativeInfinity;
  let upperBound = positiveInfinity;
  let needToTruncate = false;
  for (var t of thresholds) {
    if (t.value !== void 0) {
      if (t.operator === THRESHOLD_OPERATOR.greaterThan) {
        upperBound = t.value.toString();
        needToTruncate = true;
      }
      if (t.operator === THRESHOLD_OPERATOR.lessThan) {
        lowerBound = t.value.toString();
        needToTruncate = true;
      }
    }
  }
  if (needToTruncate) {
    return MDL_METHOD.Truncate + addParentheses([innerMDL, lowerBound, upperBound]);
  }
  return innerMDL;
};
const addAggregations = (aggregation, mdlMetricDef) => {
  if (aggregation.type === AGGREGATION_TYPE.average) {
    return AGGREGATION_METHOD.average + addParentheses([mdlMetricDef]);
  } else if (aggregation.type === AGGREGATION_TYPE.percentile && aggregation.percentile !== void 0) {
    return AGGREGATION_METHOD.percentile + addParentheses([mdlMetricDef, aggregation.percentile.toString()]);
  } else if (aggregation.type === AGGREGATION_TYPE.weightedAverage && aggregation.weightedBy) {
    return AGGREGATION_METHOD.weightedAverage + addParentheses([mdlMetricDef, aggregation.weightedBy]);
  }
  return mdlMetricDef;
};
const buildMethod = (method, level) => {
  let fullPrefix = "";
  try {
    fullPrefix = method.toString();
  } catch (e) {
    throw new Error(`Cannot parse MDL`);
  }
  if (level) {
    fullPrefix = fullPrefix + "<" + level + ">";
  }
  return fullPrefix;
};
const booleanToInt = (booleanVariableName) => {
  return booleanVariableName + " ? 1 : 0";
};
export {
  FillNull_Dict,
  FillZero_Dict,
  addAggregations,
  addThresholds,
  booleanToInt,
  buildMethod,
  buildMetricDefinition,
  mdlMetricDefinitionGenerator
};
//# sourceMappingURL=MdlMetricDefinitionGenerator.js.map
